import { signal } from "@preact/signals-react";
import type { RoomProxy } from "./roomProxy.types";
import { createLocalRoomProxy } from "./localRoomProxy";
import { createCloudRoomProxy, type CloudRoomProxyEnv } from "./cloudRoomProxy";

import type { GameCatalogClient } from '@merels/room'
import type { AuthDep } from '../../auth/auth.types'

export interface RoomProxyManager {
  currentRoomProxy: ReturnType<typeof signal<RoomProxy | null>>;
  makeNewRoomProxy(roomId: string, config?: { workerUrl: string; clientId: string; env?: CloudRoomProxyEnv }): void;
}

import { isLocalModeFromRoomName, parseRoomId } from '../../mode/urlInfo/urlInfo';

export function makeRoomProxyManager(gameCatalog: GameCatalogClient, auth?: AuthDep): RoomProxyManager {
  const currentRoomProxy = signal<RoomProxy | null>(null);

  const makeNewRoomProxy = (roomId: string, config?: { workerUrl: string; clientId: string; env?: CloudRoomProxyEnv }): void => {
    // Destroy current proxy if it exists
    const current = currentRoomProxy.value;
    if (current) {
      current.destroyProxy();
    }

    let newProxy: RoomProxy;
    const { id: realRoomId } = parseRoomId(roomId);

    if (isLocalModeFromRoomName(roomId)) {
      newProxy = createLocalRoomProxy(realRoomId, gameCatalog, config?.clientId);
    } else {
        if (!config || !config.workerUrl || !config.clientId) {
            throw new Error("workerUrl and clientId are required for cloud rooms");
        }
        newProxy = createCloudRoomProxy({
            roomId: realRoomId,
            workerUrl: config.workerUrl,
            clientId: config.clientId,
            auth,
            env: config.env,
        });
    }

    currentRoomProxy.value = newProxy;
  };

  return {
    currentRoomProxy,
    makeNewRoomProxy
  };
}
